import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	CampaignViewModel,
	ContentCalenderSuggestionViewModel,
	IAccount,
	IBlogReportView,
	IContentCalendarSuggestion,
	IImpersonationContext,
	IScheduledSend,
	ITemplate,
	PostStatus,
	SocialMediaPostReportViewModel,
	TemplateType,
} from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { Provider, inject, observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	IImpersonationContextComponentProps,
	ILocationState,
	IMultiCampaignSchedulerRequest,
	ISequenceTransitionRequest,
	TemplateOrTemplateFilter,
} from '../../../../models';
import {
	CampaignType,
	IAdminUserSessionComponentProps,
	IEditCampaignRequest,
	MastheadPortalId,
} from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { BlogPostRequest } from '../../../../models/Blogs';
import { storagePrefixExcludedFromClear } from '../../../../models/Storage';
import { usePersistChangeInBrowserStorage } from '../../../../models/hooks/usePersistChangeInBrowserStorage';
import { getBlogPostAsync, getBlogPostTemplateAsync } from '../../../../queries';
import {
	AccountCampaignViewModel,
	AccountOperationsViewModel,
	ImpersonationContextViewModel,
	SocialAccountCampaignViewModel,
} from '../../../../viewmodels/AdminViewModels';
import {
	AdminDrawerToolbar,
	AppDrawerContent,
	AppDrawerToolbarButton,
	SideDrawer,
	useAppDrawerContext,
} from '../../../../web/components-v2/Drawer/AppDrawer';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { EventDataType, ICampaignCalendarOptions } from '../../../../web/components/campaigns/CampaignCalendar/models';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { StarIcon } from '../../../../web/components/svgs/icons/StarIcon';
import { TextSearchIcon } from '../../../../web/components/svgs/icons/TextSearchIcon';
import { ContentCalendar, IContentCalendarComponent } from '../../../../web/containers/ContentCalendar';
import { ContentCalendarTemplateBrowserWrapper } from '../../../../web/containers/ContentCalendar/ContentCalendarTemplateBrowserWrapper';
import { ContentCalendarAssistantChat } from '../../../../web/containers/templates/AssistantChat/ContentCalendarAssistantChat';
import { AdminDashboardPageLayout } from '../../AdminDashboardPageLayout';
import { BlogApprovalReminderComposerModal } from '../../blogs/BlogApprovalReminderComposer';
import { BulkEmailApprovalReminderComposerModal } from '../../bulkEmailApproval/BulkEmailApprovalReminderComposer';
import { SocialApprovalReminderComposerModal } from '../../socialMedia/SocialApprovalReminderComposer';
import { GoogleReviewsSidebarComponent } from './GoogleReviewsSidebarComponent';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IAdminUserSessionComponentProps,
		RouteComponentProps<any>,
		IFullscreenModalComponentProps,
		IErrorMessageComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	onSuggestionsToggleChanged?: (isOn: boolean) => void;
	onGeneratedContentToggleChanged?: (isOn: boolean) => void;
}

interface IState {
	accountId?: string;
	accountOperations?: AccountOperationsViewModel;
	campaignsForApprovalRequestReminderComposer?: AccountCampaignViewModel[];
	socialPostsForApprovalRequestReminderComposer?: SocialAccountCampaignViewModel[];
	blogReportViewsForApprovalReminder?: IBlogReportView[];
	/** Synced from ContentCalendarFunctionalWrapper for use in side-drawer template browser */
	templateBrowserCampaignType?: CampaignType;
	setTemplateBrowserCampaignType?: (t: CampaignType) => void;
	/** Synced from ContentCalendar suggestions toggle for side-drawer template browser */
	isSuggestionsOn?: boolean;
	/** Synced from ContentCalendar generated content toggle for side-drawer template browser */
	isGeneratedContentOn?: boolean;
	/** Synced from ContentCalendar user filter for side-drawer draft queries */
	selectedUserId?: string | null;
	/** When set, opens the AI chat drawer and loads this conversation */
	selectedAIConversationId?: string;
}

interface IContentCalendarFunctionalWrapperProps extends IProps {
	onOpenMultiSuggestionsScheduler: (
		campaignType: CampaignType,
		suggestions: IContentCalendarSuggestion[],
		onFinish?: () => void,
		sequenceFlags?: { isSequenceStep?: boolean }
	) => boolean;
	onOpenSequenceTransition: (transitionRequest: ISequenceTransitionRequest) => boolean;
	onBlogPostClicked: (e: React.MouseEvent<HTMLElement>, blogReportView: IBlogReportView) => Promise<void>;
	onCampaignClicked: (e: React.MouseEvent<HTMLElement>, campaign: CampaignViewModel) => void;
	onCampaignGroupClicked: (e: React.MouseEvent<HTMLElement>, campaignGroup: CampaignViewModel[]) => void;
	onNotifyClientClicked: (campaign: EventDataType) => void;
	onOpenCampaignComposer: (template: ITemplate, schedule?: IScheduledSend, referrer?: string) => boolean;
	onRenderHeaderAccessory: () => React.ReactNode;
	onSocialMediaPostClicked: (
		e: React.MouseEvent<HTMLElement>,
		postReport: SocialMediaPostReportViewModel
	) => Promise<void>;
	onSuggestionClicked: (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		suggestion: IContentCalendarSuggestion
	) => Promise<void>;
	onSuggestedSocialMediaEventClicked: (
		e: React.MouseEvent<HTMLElement>,
		suggestion: IContentCalendarSuggestion
	) => Promise<void>;
	calendarOptions: ICampaignCalendarOptions;
	account?: IAccount;
	/** When provided (e.g. admin with drawer), parent receives template browser state to pass to side drawer */
	onTemplateBrowserStateSync?: (
		templateBrowserCampaignType: CampaignType,
		setTemplateBrowserCampaignType: (t: CampaignType) => void
	) => void;
	/** When provided (e.g. admin with drawer), parent receives suggestions toggle value for side drawer */
	onSuggestionsToggleChanged?: (isOn: boolean) => void;
	/** When provided (e.g. admin with drawer), parent receives selected user filter for side drawer draft queries */
	onSelectedUserIdChanged?: (selectedUserId: string | null) => void;
}

const ContentCalendarFunctionalWrapper: React.FC<IContentCalendarFunctionalWrapperProps> = ({
	className,
	styles,
	onOpenMultiSuggestionsScheduler,
	onOpenSequenceTransition,
	onBlogPostClicked,
	onCampaignClicked,
	onCampaignGroupClicked,
	onNotifyClientClicked,
	onOpenCampaignComposer,
	onRenderHeaderAccessory,
	onSocialMediaPostClicked,
	onSuggestionClicked,
	onSuggestedSocialMediaEventClicked,
	onGeneratedContentToggleChanged,
	calendarOptions,
	account,
	onTemplateBrowserStateSync,
	onSuggestionsToggleChanged,
	onSelectedUserIdChanged,
	...restProps
}) => {
	const contentCalendarRef = useRef<IContentCalendarComponent>();

	const onCalendarRef = useCallback((ref?: IContentCalendarComponent) => {
		contentCalendarRef.current = ref;
	}, []);

	const isBlogsEnabled = account?.features?.blogFeature?.enabled;
	const isSocialMediaEnabled = account?.features?.socialMedia?.enabled;

	const campaignTypes = React.useMemo<CampaignType[]>(() => {
		const types = [CampaignType.Email];
		if (isSocialMediaEnabled) {
			types.push(CampaignType.Social);
		}
		if (isBlogsEnabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}, [isBlogsEnabled, isSocialMediaEnabled]);

	const [templateBrowserCampaignType, setTemplateBrowserCampaignType] = usePersistChangeInBrowserStorage<CampaignType>({
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-template-campaign-type`,
		storageType: 'localStorage',
		defaultIfEmpty: CampaignType.Email,
		// skipPersistFn: () => props.impersonationContext?.isValid,
		setInitialValue: storedValue => {
			if (!campaignTypes.includes(storedValue)) {
				return campaignTypes[0];
			}
			return storedValue;
		},
	});

	React.useEffect(() => {
		onTemplateBrowserStateSync?.(templateBrowserCampaignType, setTemplateBrowserCampaignType);
	}, [templateBrowserCampaignType, setTemplateBrowserCampaignType, onTemplateBrowserStateSync]);

	return (
		<ContentCalendar
			{...restProps}
			onSuggestionsToggleChanged={onSuggestionsToggleChanged}
			onOpenMultiSuggestionsScheduler={onOpenMultiSuggestionsScheduler}
			onOpenSequenceTransition={onOpenSequenceTransition}
			calendarOptions={calendarOptions}
			onBlogPostClicked={onBlogPostClicked}
			onCampaignClicked={onCampaignClicked}
			onCampaignGroupClicked={onCampaignGroupClicked}
			ref={onCalendarRef}
			onNotifyClientClicked={onNotifyClientClicked}
			onOpenCampaignComposer={onOpenCampaignComposer}
			onRenderHeaderAccessory={onRenderHeaderAccessory}
			onSocialMediaPostClicked={onSocialMediaPostClicked}
			onSuggestionClicked={onSuggestionClicked}
			onSuggestedSocialMediaEventClicked={onSuggestedSocialMediaEventClicked}
			templateBrowserCampaignType={templateBrowserCampaignType}
			setTemplateBrowserCampaignType={setTemplateBrowserCampaignType}
			hideTemplateBrowser={true}
			onGeneratedContentToggleChanged={onGeneratedContentToggleChanged}
			onSelectedUserIdChanged={onSelectedUserIdChanged}
		/>
	);
};

const AIDrawerOpener: React.FC<{ conversationId?: string }> = ({ conversationId }) => {
	const { setValue } = useAppDrawerContext();
	useEffect(() => {
		if (conversationId) {
			setValue('ai-assistant');
		}
	}, [conversationId, setValue]);
	return null;
};

class _AccountContentCalendar extends React.Component<IProps, IState> {
	public readonly state: IState = {
		campaignsForApprovalRequestReminderComposer: [],
	};
	private static calendarOptions: ICampaignCalendarOptions = {
		timeSpanOptions: {
			eventOptions: {
				hideApprovalCtas: true,
				showScheduledFor: true,
			},
			groupEventsByGroupId: true,
		},
	};

	private mContentCalendarRef: IContentCalendarComponent;

	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new ImpersonationContextViewModel(),
	};

	public static getDerivedStateFromProps(props: Readonly<IProps>, state: IState) {
		const nextState: IState = {};
		const accountId = props.match.params?.id;
		if (accountId !== state.accountId) {
			nextState.accountId = accountId;
		}
		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	public async componentDidMount() {
		const id = this.urlAccountId;
		if (id) {
			this.setState({ accountId: id });
		}
	}

	public componentDidUpdate() {
		const { accountId, accountOperations } = this.state;
		if (accountOperations?.account?.id !== accountId) {
			this.loadAccount(accountId);
		}
	}

	private onGeneratedContentToggleChanged = (isOn: boolean) => {
		this.setState({ isGeneratedContentOn: isOn });
	};

	public render() {
		const { className, styles, ...restProps } = this.props;
		const {
			campaignsForApprovalRequestReminderComposer,
			socialPostsForApprovalRequestReminderComposer,
			blogReportViewsForApprovalReminder,
			templateBrowserCampaignType,
			setTemplateBrowserCampaignType,
			isSuggestionsOn,
			isGeneratedContentOn,
			selectedUserId,
			selectedAIConversationId,
		} = this.state;
		const account = this.mImpersonationProviderProps?.impersonationContext.account;
		const campaignTypes = this.getCampaignTypesForAccount(account);

		const mainContent = (
			<div className={`${css(styleSheet.container, ...(styles || []))} account-content-calendar ${className || ''}`}>
				<MultiContainerHeader appBarHeader={this.renderAppBarHeader()} appBarPortalDestinationId={MastheadPortalId} />

				{account && (
					<>
						<ContentCalendarFunctionalWrapper
							{...restProps}
							onOpenMultiSuggestionsScheduler={this.onOpenMultiSuggestionsScheduler}
							onOpenSequenceTransition={this.onOpenSequenceTransition}
							calendarOptions={_AccountContentCalendar.calendarOptions}
							onBlogPostClicked={this.onBlogPostClicked}
							onCampaignClicked={this.onCampaignClicked}
							onCampaignGroupClicked={this.onCampaignGroupClicked}
							onNotifyClientClicked={this.onNotifyClientClicked}
							onOpenCampaignComposer={this.onOpenCampaignComposer}
							onRenderHeaderAccessory={this.onRenderHeaderAccessory}
							onSocialMediaPostClicked={this.onSocialMediaPostClicked}
							onSuggestionClicked={this.onSuggestionClicked}
							onSuggestedSocialMediaEventClicked={this.onSuggestedSocialMediaEventClicked}
							account={account}
							onTemplateBrowserStateSync={this.onTemplateBrowserStateSync}
							onSuggestionsToggleChanged={this.onSuggestionsToggleChanged}
							onGeneratedContentToggleChanged={this.onGeneratedContentToggleChanged}
							onSelectedUserIdChanged={this.onSelectedUserIdChanged}
						/>
						<BulkEmailApprovalReminderComposerModal
							campaigns={campaignsForApprovalRequestReminderComposer.map(c => c.toJs())}
							modalProps={{
								className: css(styleSheet.approvalReminderModal),

								isOpen: campaignsForApprovalRequestReminderComposer.length > 0,
								onRequestClose: this.onSendApprovalRequestReminderComposerRequestClose,
							}}
						/>
						<SocialApprovalReminderComposerModal
							campaigns={socialPostsForApprovalRequestReminderComposer}
							modalProps={{
								className: css(styleSheet.approvalReminderModal),

								isOpen: socialPostsForApprovalRequestReminderComposer?.length > 0,
								onRequestClose: this.onSendSocialApprovalRequestReminderComposerRequestClose,
							}}
						/>
						<BlogApprovalReminderComposerModal
							blogReportViews={blogReportViewsForApprovalReminder}
							modalProps={{
								className: css(styleSheet.approvalReminderModal),
								isOpen: blogReportViewsForApprovalReminder?.length > 0,
								onRequestClose: this.onSendBlogApprovalRequestReminderComposerRequestClose,
							}}
						/>
					</>
				)}
			</div>
		);

		const sidebarChildren = (
			<SideDrawer>
				<AIDrawerOpener conversationId={selectedAIConversationId} />
				<AppDrawerContent value={isGeneratedContentOn && isSuggestionsOn ? 'ai-assistant' : undefined}>
					<ContentCalendarAssistantChat
						impersonationContext={
							account
								? {
										...this.mImpersonationProviderProps.impersonationContext.toJs(),
										...(selectedUserId ? { user: { id: selectedUserId } } : {}),
									}
								: { account: { id: this.urlAccountId } }
						}
						initialConversationId={selectedAIConversationId}
					/>
				</AppDrawerContent>

				<AppDrawerContent value='templates'>
					<ContentCalendarTemplateBrowserWrapper
						templateBrowserCampaignType={templateBrowserCampaignType ?? CampaignType.Email}
						setTemplateBrowserCampaignType={setTemplateBrowserCampaignType ?? this.noopSetTemplateBrowserCampaignType}
						isSuggestionsOn={isSuggestionsOn ?? false}
						onCampaignTemplateClicked={this.onOpenCampaignComposer}
						campaignTypes={campaignTypes}
						selectedUserId={selectedUserId}
						impersonationContext={this.mImpersonationProviderProps.impersonationContext?.toJs()}
					/>
				</AppDrawerContent>

				{account?.features?.socialMedia?.enabled && account?.features?.surveys?.enabled && (
					<AppDrawerContent value='google-reviews'>
						<GoogleReviewsSidebarComponent
							impersonationContext={this.mImpersonationProviderProps.impersonationContext}
							accountId={this.urlAccountId}
						/>
					</AppDrawerContent>
				)}

				<AdminDrawerToolbar>
					<AppDrawerToolbarButton disabled={!isGeneratedContentOn} value='ai-assistant' tooltip='AI Assistant'>
						<SparklesIcon />
					</AppDrawerToolbarButton>
					<AppDrawerToolbarButton value='templates' tooltip='Browse templates'>
						<TextSearchIcon />
					</AppDrawerToolbarButton>
					{account?.features?.socialMedia?.enabled && account?.features?.surveys?.enabled && (
						<AppDrawerToolbarButton value='google-reviews' tooltip='Google Reviews'>
							<StarIcon fillColor='currentColor' />
						</AppDrawerToolbarButton>
					)}
				</AdminDrawerToolbar>
			</SideDrawer>
		);

		const layout = <AdminDashboardPageLayout sidebarChildren={sidebarChildren}>{mainContent}</AdminDashboardPageLayout>;

		return account ? (
			<Provider
				{...this.mImpersonationProviderProps}
				key={this.mImpersonationProviderProps.impersonationContext.account.id}
			>
				{layout}
			</Provider>
		) : (
			layout
		);
	}

	private getCampaignTypesForAccount(account?: IAccount): CampaignType[] {
		const types = [CampaignType.Email];
		if (account?.features?.socialMedia?.enabled) {
			types.push(CampaignType.Social);
		}
		if (account?.features?.blogFeature?.enabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}

	private onTemplateBrowserStateSync = (
		templateBrowserCampaignType: CampaignType,
		setTemplateBrowserCampaignType: (t: CampaignType) => void
	) => {
		this.setState({ templateBrowserCampaignType, setTemplateBrowserCampaignType });
	};

	private noopSetTemplateBrowserCampaignType = (_t: CampaignType) => {
		// No-op until ContentCalendarFunctionalWrapper syncs state
	};

	private onSuggestionsToggleChanged = (isOn: boolean) => {
		this.setState({ isSuggestionsOn: isOn });
	};

	private onSelectedUserIdChanged = (selectedUserId: string | null) => {
		this.setState({ selectedUserId });
	};

	private renderAppBarHeader() {
		return (
			<NavigationBreadcrumbsBar
				currentLocationName='Content Calendar'
				pathComponentNameProvider={this.appBarHeaderPathComponentNameProvider}
			/>
		);
	}

	private appBarHeaderPathComponentNameProvider = (pathComponent: string) => {
		const { accountOperations } = this.state;
		if (accountOperations && accountOperations.account?.id === pathComponent) {
			return accountOperations.account.companyName;
		}

		if (this.urlAccountId && pathComponent === this.urlAccountId) {
			return 'Loading account...';
		}

		return pathComponent
			? pathComponent
					.split(' ')
					.filter(x => !!x)
					.map(x => `${x.substring(0, 1).toLocaleUpperCase()}${x.substring(1)}`)
					.join(' ')
			: pathComponent;
	};

	private get urlAccountId() {
		const { match } = this.props;
		return match.params?.id;
	}

	private onRenderHeaderAccessory = (): React.ReactNode => {
		// User filtering is now handled by the main content calendar user dropdown
		// No need for a separate "Levitate Users" dropdown
		return null;
	};

	private onOpenMultiSuggestionsScheduler = (
		campaignType: CampaignType,
		suggestions: IContentCalendarSuggestion[],
		onFinish?: () => void,
		sequenceFlags?: { isSequenceStep?: boolean }
	) => {
		const { fullscreenModal } = this.props;
		const { accountOperations } = this.state;

		const impersonationContext = this.mImpersonationProviderProps.impersonationContext.toJs();

		const locationState: ILocationState<any, IMultiCampaignSchedulerRequest> = {
			model: {
				suggestions,
				impersonationContext,
				onFinish,
				...sequenceFlags,
			},
		};

		let pathname = `/accounts/${accountOperations?.account?.id}/content-calendar/multi-campaign-scheduler`;
		switch (campaignType) {
			case CampaignType.Email: {
				pathname = `${pathname}/email`;
				break;
			}
			case CampaignType.Social: {
				pathname = `${pathname}/social`;
				break;
			}
			case CampaignType.Blog: {
				pathname = `${pathname}/blogs`;
				break;
			}
			default: {
				break;
			}
		}

		fullscreenModal.history.push({
			pathname,
			state: locationState,
		});
		return true;
	};

	private onOpenSequenceTransition = (transitionRequest: ISequenceTransitionRequest) => {
		const { fullscreenModal } = this.props;
		const { accountOperations } = this.state;
		fullscreenModal.history.push({
			pathname: `/accounts/${accountOperations?.account?.id}/content-calendar/multi-campaign-scheduler/transition`,
			state: { model: transitionRequest } as ILocationState<any, ISequenceTransitionRequest>,
		});
		return true;
	};

	private onCalendarRef = (ref?: IContentCalendarComponent) => {
		this.mContentCalendarRef = ref;
	};

	private loadAccount = async (accountId: string) => {
		const { userSession, logEvent, logApiError } = this.props;

		const accountOperations = new AccountOperationsViewModel(userSession, {
			id: accountId,
		});
		try {
			logEvent('AccountOperationsLoad');
			await accountOperations.load();
			this.setState(
				{
					accountOperations,
				},
				() => {
					this.mImpersonationProviderProps.impersonationContext.update({
						account: accountOperations.account,
					});
					document.title = `Admin | ${accountOperations.account.companyName}`;
				}
			);
		} catch (error) {
			logApiError('AccountOperationsLoad-Error', error);
		}
	};

	private onCampaignGroupClicked = (e: React.MouseEvent<HTMLElement>, campaignGroup: CampaignViewModel[]) => {
		e.preventDefault();
		const { fullscreenModal } = this.props;
		const locationState: ILocationState<any, IEditCampaignRequest> = {
			model: {
				campaignGroup,
				impersonationContext: this.mImpersonationProviderProps.impersonationContext,
				referrer: 'accountContentCalendar',
			},
		};
		fullscreenModal?.history?.push({
			pathname: '/email/campaigns/edit',
			state: locationState,
		});
	};

	private onCampaignClicked = (e: React.MouseEvent<HTMLElement>, campaign: CampaignViewModel) => {
		e.preventDefault();
		const { fullscreenModal } = this.props;
		const locationState: ILocationState<any, IEditCampaignRequest> = {
			model: {
				campaign,
				impersonationContext: this.mImpersonationProviderProps.impersonationContext,
				referrer: 'accountContentCalendar',
			},
		};
		fullscreenModal?.history?.push({
			pathname: '/email/campaigns/edit',
			state: locationState,
		});
	};

	private onBlogPostClicked = async (e: React.MouseEvent<HTMLElement>, blogReportView: IBlogReportView) => {
		e.preventDefault();
		const { fullscreenModal, userSession, errorMessages } = this.props;
		try {
			const impersonationCtx: IImpersonationContext = {
				account: this.mImpersonationProviderProps.impersonationContext?.account,
				user: blogReportView.creator,
			};
			const post = await getBlogPostAsync({
				id: blogReportView.id,
				impersonationContext: impersonationCtx,
				userSession,
			});
			const locationState: ILocationState<any, BlogPostRequest> = {
				model: {
					post,
					impersonationContext: impersonationCtx,
					sendFromUser: impersonationCtx.user,
				},
			};
			fullscreenModal?.history?.push({
				pathname: `/accounts/${impersonationCtx?.account?.id}/content-calendar/blog/post/edit/${post.id}`,
				state: locationState,
			});
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};

	private onSocialMediaPostClicked = async (
		e: React.MouseEvent<HTMLElement>,
		postReport: SocialMediaPostReportViewModel
	) => {
		e.preventDefault();
		const { fullscreenModal } = this.props;
		const { accountId } = this.state;
		const impersonationContext = new ImpersonationContextViewModel(
			this.mImpersonationProviderProps.impersonationContext
		);

		impersonationContext.user = postReport.creator;

		const locationState: ILocationState<SocialMediaPostReportViewModel, any> = {
			viewModel: postReport.impersonate(impersonationContext),
		};
		const path: string =
			postReport?.status === PostStatus.Pending
				? `/accounts/${accountId}/content-calendar/post-approval/approval-preview`
				: `/accounts/${accountId}/content-calendar/post/edit-from-post-report`;
		fullscreenModal?.history.push({
			pathname: path,
			state: locationState,
		});
	};

	private onSuggestionClicked = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		suggestion: IContentCalendarSuggestion
	) => {
		const { logApiError, fullscreenModal, userSession, errorMessages } = this.props;
		const { accountId } = this.state;
		if (!e.defaultPrevented) {
			e.preventDefault();
		}

		if (suggestion.aiConversationId) {
			this.setState({ selectedAIConversationId: suggestion.aiConversationId });
			return;
		}

		if (suggestion.templateReference.templateType === TemplateType.Blog) {
			try {
				const result = await getBlogPostTemplateAsync({
					id: suggestion.templateReference.templateId,
					userSession,
					impersonationContext: this.mImpersonationProviderProps.impersonationContext?.toJs(),
				});
				if (!result.contentCalendarSuggestionId) {
					result.contentCalendarSuggestionId = suggestion.id;
				}
				const locationState: ILocationState<any, BlogPostRequest> = {
					model: {
						impersonationContext: this.mImpersonationProviderProps.impersonationContext?.toJs(),
						template: result,
						schedule: suggestion.schedule,
					},
				};
				fullscreenModal?.history?.push({
					pathname: `/accounts/${accountId}/content-calendar/blog/post/create-from-template/${result.id}`,
					state: locationState,
				});
			} catch (error) {
				errorMessages.pushApiError(error);
			}
			return;
		}

		const viewModel = new ContentCalenderSuggestionViewModel(userSession, suggestion).impersonate(
			this.mImpersonationProviderProps.impersonationContext
		);
		try {
			await viewModel.load();
			const locationState: ILocationState<any, ICreateCampaignRequest<ITemplate>> = {
				model: {
					context: viewModel.template,
					onFinish: (didSend: boolean) => {
						if (didSend) {
							this.mContentCalendarRef?.loadAllCalendarData();
						}
					},
					schedule: viewModel.schedule,

					sendFromUser: this.mImpersonationProviderProps.impersonationContext.user,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${accountId}/content-calendar/campaigns/create`,
				state: locationState,
			});
		} catch (error) {
			logApiError('LoadSuggestion-Error', error);
		}
	};

	private onSuggestedSocialMediaEventClicked = async (
		e: React.MouseEvent<HTMLElement>,
		suggestion: IContentCalendarSuggestion
	) => {
		const { logApiError, fullscreenModal } = this.props;
		const { accountId } = this.state;
		if (!e.defaultPrevented) {
			e.preventDefault();
		}

		if (suggestion.aiConversationId) {
			this.setState({ selectedAIConversationId: suggestion.aiConversationId });
			return;
		}

		const templateId = suggestion?.templateReference?.templateId;
		const suggestionId = suggestion?.id;
		try {
			const locationState: ILocationState<any, ICreateSocialMediaPostRequest<ITemplate | string>> = {
				model: {
					context: templateId,
					impersonationContext: {
						account: this.mImpersonationProviderProps.impersonationContext.account,

						user: this.mImpersonationProviderProps.impersonationContext.user,
					},
					onFinish: (didSend: boolean) => {
						if (didSend) {
							this.mContentCalendarRef?.loadAllCalendarData();
						}
					},
					schedule: suggestion?.schedule,

					sendFromUser: this.mImpersonationProviderProps.impersonationContext.user,
					targets: suggestion?.targets,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${accountId}/content-calendar/post/suggestion/preview-edit/${templateId}/${suggestionId}}`,
				state: locationState,
			});
		} catch (error) {
			logApiError('LoadSuggestion-Error', error);
		}
	};

	private onOpenCampaignComposer = (template: ITemplate, schedule?: IScheduledSend, referrer?: string) => {
		const { fullscreenModal } = this.props;
		const { accountId } = this.state;

		switch (template?.templateType) {
			case TemplateType.Email: {
				const locationStateEmail: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
					model: {
						context: template,
						onFinish: (didSend: boolean) => {
							if (didSend) {
								this.mContentCalendarRef?.loadAllCalendarData();
							}
						},
						schedule,

						sendFromUser: this.mImpersonationProviderProps.impersonationContext.user,
						type: 'Template',
					},
					referrer,
				};
				fullscreenModal?.history.push({
					pathname: `/accounts/${accountId}/content-calendar/campaigns/create`,
					state: locationStateEmail,
				});
				break;
			}
			case TemplateType.SocialMediaPost: {
				const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<ITemplate>> = {
					model: {
						context: template,
						impersonationContext: {
							account: this.mImpersonationProviderProps.impersonationContext.account,

							user: this.mImpersonationProviderProps.impersonationContext.user,
						},
						onFinish: (didSend: boolean) => {
							if (didSend) {
								this.mContentCalendarRef?.loadAllCalendarData();
							}
						},
						schedule,

						sendFromUser: this.mImpersonationProviderProps.impersonationContext.user,
						type: 'Template',
					},
					referrer,
				};

				fullscreenModal?.history.push({
					pathname: `/accounts/${accountId}/content-calendar/post/create`,
					state: locationStateSocial,
				});
				break;
			}
			case TemplateType.Blog: {
				const impersonationCtx = this.mImpersonationProviderProps.impersonationContext.toJs();
				const locationStateSocial: ILocationState<any, BlogPostRequest> = {
					model: {
						template,
						impersonationContext: impersonationCtx,
						onFinish: (success: boolean) => {
							if (success) {
								this.mContentCalendarRef?.loadAllCalendarData();
							}
						},
						schedule,
						sendFromUser: impersonationCtx.user,
					},
					referrer,
				};

				fullscreenModal?.history.push({
					pathname: `/accounts/${accountId}/content-calendar/blog/post/create-from-template/${template.id}`,
					state: locationStateSocial,
				});
				break;
			}
			default: {
				break;
			}
		}

		return true;
	};

	private onNotifyClientClicked = (campaign: EventDataType) => {
		const { userSession } = this.props;
		const { accountOperations } = this.state;
		if (campaign instanceof SocialMediaPostReportViewModel) {
			this.setState({
				socialPostsForApprovalRequestReminderComposer: [
					new SocialAccountCampaignViewModel(userSession, campaign.toJs()).impersonate({
						account: accountOperations.account,
						user: campaign.creator,
					}),
				],
			});
		} else {
			if (Array.isArray(campaign)) {
				const campaignsForApprovalRequestReminderComposer = campaign.map(x =>
					new AccountCampaignViewModel(userSession, x.toJs()).impersonate({
						account: accountOperations.account,
						user: x.creator,
					})
				);
				this.setState({ campaignsForApprovalRequestReminderComposer });
			} else if (campaign instanceof CampaignViewModel) {
				this.setState({
					campaignsForApprovalRequestReminderComposer: [
						new AccountCampaignViewModel(userSession, campaign.toJs()).impersonate({
							account: accountOperations.account,
							user: campaign.creator,
						}),
					],
				});
			} else if (campaign._type === 'BlogReportView') {
				this.setState({
					blogReportViewsForApprovalReminder: [campaign],
				});
			}
		}
	};

	private onSendApprovalRequestReminderComposerRequestClose = () => {
		this.setState({
			campaignsForApprovalRequestReminderComposer: [],
		});
	};
	private onSendSocialApprovalRequestReminderComposerRequestClose = () => {
		this.setState({
			socialPostsForApprovalRequestReminderComposer: null,
		});
	};
	private onSendBlogApprovalRequestReminderComposerRequestClose = () => {
		this.setState({
			blogReportViewsForApprovalReminder: null,
		});
	};
}

const AccountContentCalendarAsObserver = observer(_AccountContentCalendar);
const AccountContentCalendarWithContext = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey,
	ErrorMessagesViewModelKey
)(AccountContentCalendarAsObserver);
export const AccountContentCalendar = withRouter(
	withEventLogging(AccountContentCalendarWithContext, 'AccountContentCalendar')
);
